import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, PartyPopper, AlertCircle } from 'lucide-react';
import Layout from '../../components/Layout';
import RatingItem from './RatingItem';
import RatingBar from './RatingBar';
import { useRatingAutosave } from './useRatingAutosave';
import { MIN_REMARKS_LENGTH, RATING_CONTENT_MAX_WIDTH } from './constants';
import {
  fetchAssignments,
  fetchNextAssignment,
  fetchProgress,
  skipAssignment,
  type AssignmentSummary,
  type RatingProgress,
} from './api';

/**
 * RLHF rating workbench.
 *
 * A rater works through a personal queue. "Next" drains their own unrated backlog before
 * claiming anything new from the pool, and every item stays in the queue afterwards so a
 * score can be revisited and changed.
 *
 * The simulation is rendered by the product's own results component, so the OUTPUT a rater
 * judges is exactly the output a customer sees. The INPUTS deliberately differ: `isRatingView`
 * turns on `userInputsOnly`, narrowing them to what the author actually filled in on the
 * use-case form and dropping request plumbing (audience targeting, model routing), and lays
 * them out beside the results rather than above. See the `userInputsOnly` prop on
 * SimulationInputsDropdown — the customer-facing page still shows the full request.
 */

const RatingWorkbench: React.FC = () => {
  const [queue, setQueue] = useState<AssignmentSummary[]>([]);
  const [index, setIndex] = useState(0);
  const [progress, setProgress] = useState<RatingProgress | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [exhausted, setExhausted] = useState(false);
  const [fatalError, setFatalError] = useState<string | null>(null);
  // Recoverable problems — shown inline, never replacing the page or the rater's work.
  const [navError, setNavError] = useState<string | null>(null);

  const [rating, setRating] = useState<number | null>(null);
  const [remarks, setRemarks] = useState('');
  // Gates autosave while we populate the form from the server, so hydrating an existing
  // rating doesn't immediately write it back.
  const [hydrated, setHydrated] = useState(false);

  const current = queue[index] ?? null;

  /**
   * Mirror every committed save into the queue, drafts included.
   *
   * The queue is what the hydration effect restores from when the rater navigates back, so if
   * drafts don't land here it goes stale: the server holds the reasoning while the queue still
   * holds null, revisiting shows an empty box, and the next keystroke writes that blank over
   * good text. Mirroring only on submit is not enough — most typing is persisted by drafts.
   */
  const mirrorToQueue = useCallback(
    (c: { itemId: number; choice: number | null; remarks: string; draft: boolean }) => {
      setQueue((prev) =>
        prev.map((a) =>
          a.id === c.itemId
            ? {
                ...a,
                rating: c.choice,
                remarks: c.remarks,
                is_rated: c.draft ? a.is_rated : true,
                status: c.draft ? a.status : 'submitted',
              }
            : a,
        ),
      );
    },
    [],
  );

  const { saveState, error: saveError, flush, setBaseline, cancelPending } = useRatingAutosave({
    assignmentId: current?.id ?? null,
    rating,
    remarks,
    enabled: hydrated,
    onSaved: mirrorToQueue,
  });

  const refreshProgress = useCallback(async () => {
    try {
      setProgress(await fetchProgress());
    } catch {
      /* progress is decorative; never block rating on it */
    }
  }, []);

  /** Load the queue, and claim a first item if the rater has nothing yet. */
  const bootstrap = useCallback(async () => {
    setLoading(true);
    try {
      let assignments = await fetchAssignments();
      if (assignments.length === 0) {
        const claimed = await fetchNextAssignment();
        if (!claimed) {
          setExhausted(true);
          setLoading(false);
          return;
        }
        assignments = await fetchAssignments();
      }
      setQueue(assignments);
      // Land on the first thing that still needs judgment rather than the top of history.
      const firstUnrated = assignments.findIndex((a) => !a.is_rated);
      setIndex(firstUnrated === -1 ? assignments.length - 1 : firstUnrated);
      void refreshProgress();
    } catch (e) {
      setFatalError(e instanceof Error ? e.message : 'Could not load your rating queue');
    } finally {
      setLoading(false);
    }
  }, [refreshProgress]);

  useEffect(() => { void bootstrap(); }, [bootstrap]);

  // Rehydrate the form whenever the current assignment changes, so revisiting a rated
  // simulation shows the score that was actually given.
  // Keyed on the assignment IDENTITY only. Depending on current.rating/current.remarks too
  // meant that locally marking an item rated re-ran this and reset the form from a stale queue
  // entry — which, combined with autosave, overwrote good remarks with an empty string.
  useEffect(() => {
    const loadedRating = current?.rating ?? null;
    const loadedRemarks = current?.remarks ?? '';
    setHydrated(false);
    setRating(loadedRating);
    setRemarks(loadedRemarks);
    // Adopt the server values as the baseline, so merely opening an item is never mistaken
    // for an edit and cannot trigger a write.
    setBaseline(current?.id ?? null, loadedRating, loadedRemarks);
    const id = setTimeout(() => setHydrated(true), 0);
    return () => clearTimeout(id);
  }, [current?.id, setBaseline]);   // eslint-disable-line react-hooks/exhaustive-deps

  const goNext = useCallback(async () => {
    if (!current) return;
    setBusy(true);
    try {
      // Commit before moving. A failed save must block navigation — advancing would
      // silently discard the rater's reasoning.
      // On success `mirrorToQueue` has already written the score and remarks into the queue
      // entry, so there is nothing further to apply locally.
      const ok = await flush(false);
      if (!ok) return;

      const nextUnrated = queue.findIndex((a, i) => i > index && !a.is_rated);
      if (nextUnrated !== -1) {
        setIndex(nextUnrated);
      } else {
        const claimed = await fetchNextAssignment();
        if (!claimed) {
          setExhausted(true);
        } else {
          const assignments = await fetchAssignments();
          setQueue(assignments);
          const landing = assignments.findIndex((a) => a.id === claimed.id);
          setIndex(landing === -1 ? assignments.length - 1 : landing);
        }
      }
      void refreshProgress();
    } catch (e) {
      // The rating itself was saved above; only the advance failed. Say so rather than
      // leaving the rater clicking a button that appears to do nothing.
      setNavError(e instanceof Error ? e.message : 'Saved, but could not load the next simulation');
    } finally {
      setBusy(false);
    }
  }, [current, flush, queue, index, refreshProgress]);

  const goPrev = useCallback(async () => {
    if (index === 0) return;
    setBusy(true);
    try {
      // Same rule as Next: never navigate away from work we failed to persist.
      if (!(await flush(true))) return;
      setIndex((i) => Math.max(0, i - 1));
    } finally {
      setBusy(false);
    }
  }, [index, flush]);

  const goToIndex = useCallback(async (target: number) => {
    if (target === index) return;
    setBusy(true);
    try {
      if (!(await flush(true))) return;
      setIndex(target);
    } finally {
      setBusy(false);
    }
  }, [index, flush]);

  const skip = useCallback(async () => {
    if (!current) return;
    // Skip is now terminal and REQUIRES a reason (captured in the remarks box). It is only offered
    // when no score is picked, so there is nothing to discard and no confirm — the reason is
    // deliberate input, not a misclick. Disarm the pending draft save so the debounce can't race
    // the skip write.
    const reason = remarks.trim();
    if (reason.length < MIN_REMARKS_LENGTH) return;
    cancelPending();
    setBusy(true);
    try {
      // Terminal skip: the item is closed out with its reason and never re-served, so — unlike the
      // old release path — there is no need to exclude it from the next claim. Advance exactly like
      // a normal submit: mark it handled in the queue, then move to the next unhandled item or
      // claim a fresh one.
      await skipAssignment(current.id, reason);
      const claimed = await fetchNextAssignment();
      const refreshed = await fetchAssignments();
      setQueue(refreshed);

      if (refreshed.length === 0) {
        setExhausted(true);
        setIndex(0);
      } else if (claimed) {
        const landing = refreshed.findIndex((a) => a.id === claimed.id);
        setIndex(landing === -1 ? refreshed.length - 1 : landing);
      } else {
        // Nothing new to claim: land on the rater's own next unhandled item, else the end.
        const pending = refreshed.findIndex((a) => !a.is_rated);
        setIndex(pending === -1 ? refreshed.length - 1 : pending);
        setExhausted(pending === -1);
      }
      void refreshProgress();
    } catch (e) {
      // Non-fatal: keep the rater on the page with their work intact.
      setNavError(e instanceof Error ? e.message : 'Could not skip this simulation');
    } finally {
      setBusy(false);
    }
  }, [current, remarks, refreshProgress, cancelPending]);

  const ratedCount = useMemo(() => queue.filter((a) => a.is_rated).length, [queue]);
  const percent = queue.length ? Math.round((ratedCount / queue.length) * 100) : 0;

  if (loading) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center py-32 gap-3">
          <Loader2 className="w-7 h-7 animate-spin text-blue-600" aria-hidden="true" />
          <p className="text-gray-500 text-sm">Loading your rating queue…</p>
        </div>
      </Layout>
    );
  }

  if (fatalError) {
    return (
      <Layout>
        <div className="max-w-md mx-auto text-center py-32">
          <AlertCircle className="w-9 h-9 text-red-500 mx-auto mb-3" aria-hidden="true" />
          <h1 className="text-lg font-semibold mb-1">Can’t start rating</h1>
          <p className="text-gray-600 text-sm mb-5">{fatalError}</p>
          <button
            type="button"
            onClick={() => { setFatalError(null); void bootstrap(); }}
            className="px-4 py-2 min-h-[44px] rounded-xl bg-gray-900 text-white text-sm font-semibold"
          >
            Try again
          </button>
        </div>
      </Layout>
    );
  }

  if (exhausted && !queue.some((a) => !a.is_rated)) {
    return (
      <Layout>
        <div className="max-w-md mx-auto text-center py-32">
          <PartyPopper className="w-9 h-9 text-blue-600 mx-auto mb-3" aria-hidden="true" />
          <h1 className="text-lg font-semibold mb-1">Nothing left to rate</h1>
          <p className="text-gray-600 text-sm">
            You’ve worked through every simulation available to you
            {progress
              ? ` — ${progress.rated} rated in total${progress.skipped ? `, ${progress.skipped} skipped` : ''}.`
              : '.'} Thank you!
          </p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className={`${RATING_CONTENT_MAX_WIDTH} mx-auto pb-56`}>
        <div className="flex justify-between items-start gap-5 flex-wrap">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.09em] text-blue-700">
              RLHF · Simulation rating
            </p>
            <h1 className="text-[22px] font-semibold tracking-tight mt-1">
              {current?.simulation_name ?? 'Simulation'}
            </h1>
            <p className="text-gray-500 text-[13px]">
              {current?.use_case ?? '—'} · assignment {index + 1} of {queue.length}
            </p>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl px-4 py-3 min-w-[210px]">
            <div className="flex justify-between items-baseline">
              <p className="text-[17px] font-semibold">
                {ratedCount} <span className="text-gray-500 text-xs font-normal">of {queue.length} rated</span>
              </p>
              <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">
                {percent}%
              </span>
            </div>
            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden mt-2">
              <div className="h-full bg-blue-500 transition-all" style={{ width: `${percent}%` }} />
            </div>
            {progress && (
              <p className="text-[11px] text-gray-500 mt-1.5">
                {progress.available} more available in the pool
              </p>
            )}
          </div>
        </div>

        {navError && (
          <div role="alert"
               className="mt-3 flex items-start gap-2 rounded-xl border border-amber-300
                          bg-amber-50 px-3 py-2 text-[13px] text-amber-900">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
            <span className="flex-1">{navError}</span>
            <button type="button" onClick={() => setNavError(null)}
                    className="font-semibold underline shrink-0">Dismiss</button>
          </div>
        )}

        <div className="flex gap-1.5 items-center mt-4 mb-1 overflow-x-auto pb-1.5">
          <span className="text-[11px] text-gray-500 mr-1 shrink-0">Your queue</span>
          {queue.map((a, i) => (
            <button
              key={a.id}
              type="button"
              onClick={() => void goToIndex(i)}
              disabled={busy}
              aria-label={`Assignment ${i + 1}${a.is_rated ? `, rated ${a.rating}` : ', not yet rated'}`}
              aria-current={i === index}
              className={`w-7 h-7 shrink-0 rounded-lg text-[11px] font-semibold border
                          transition-colors disabled:opacity-50 ${
                i === index
                  ? 'bg-gray-900 border-gray-900 text-white'
                  : a.is_rated
                    ? 'bg-blue-50 border-blue-200 text-blue-700'
                    : 'bg-white border-gray-200 text-gray-500'
              }`}
            >
              {i + 1}
            </button>
          ))}
        </div>

        {current && (
          <div className="mt-3">
            {/* The item is a simulation or an hcv2 chat session; RatingItem renders by kind. The
                assignment id (not the item id) scopes the endpoint. */}
            <RatingItem
              key={current.id}
              keyId={current.id}
              itemRef={current.simulation_id}
            />
          </div>
        )}
      </div>

      <RatingBar
        rating={rating}
        remarks={remarks}
        onRatingChange={setRating}
        onRemarksChange={setRemarks}
        onPrev={() => void goPrev()}
        onNext={() => void goNext()}
        onSkip={() => void skip()}
        canGoPrev={index > 0}
        isLast={index === queue.length - 1}
        busy={busy}
        saveState={saveState}
        error={saveError}
      />
    </Layout>
  );
};

export default RatingWorkbench;
